// client/src/utils/useInventoryData.js
import { useState, useEffect, useCallback } from "react";
import { useAuth } from '../AuthContext';
import { toNumberOrNull, toIntOr } from '../utils/formatters';

export default function useInventoryData({ saleId, clientId, sectionId }) {
  const [inventory, setInventory] = useState([]);
  const [deletedItems, setDeletedItems] = useState([]);
  const [saleMeta, setSaleMeta] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');

  const { fetchWrapper, supabase } = useAuth();

  const generateSignedUrl = useCallback(async (path) => {
    try {
      const { data, error } = await supabase.storage.from('images').createSignedUrl(path, 7776000);
      if (error) throw error;
      return data.signedUrl || 'N/A';
    } catch (err) {
      console.error('Signed URL error:', err);
      return 'N/A';
    }
  }, [supabase]);

  const updateCachedUrl = useCallback(async (uploadLogId, signedUrl, urlExpiry) => {
    try {
      await fetchWrapper(
        `${process.env.REACT_APP_API_URL}/supabase/rpc/update_signed_urls_by_upload_log_id`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            p_upload_log_id: uploadLogId,
            p_signed_url: signedUrl,
            p_url_expiry: urlExpiry,
          }),
        }
      );
    } catch (err) {
      console.error('Update cached URL error:', err);
    }
  }, [fetchWrapper]);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus('Loading inventory...');
    try {
      if (!saleId || !clientId) {
        setInventory([]);
        setDeletedItems([]);
        setSaleMeta(null);
        setSections([]);
        setStatus('');
        return;
      }

      // Inventory RPC
      const itemBody = {
        p_sale_id: saleId,
        p_client_id: clientId,
        p_section_id: sectionId !== 'all' ? sectionId : null,
      };
      const itemResponse = await fetchWrapper(
        `${process.env.REACT_APP_API_URL}/supabase/rpc/select_inventory_items_by_sale_id`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(itemBody),
        }
      );
      const items = await itemResponse.json();

      // Changes RPC
      const changesBody = {
        p_sale_id: saleId,
        p_client_id: clientId,
      };
      const changesResponse = await fetchWrapper(
        `${process.env.REACT_APP_API_URL}/supabase/rpc/select_item_changes_by_sale_id`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(changesBody),
        }
      );
      const changes = await changesResponse.json();

      if (itemResponse.ok && Array.isArray(items) && changesResponse.ok && Array.isArray(changes)) {
        // Map changes by inventory_item_id
        const changesByItemId = {};
        changes.forEach(change => {
          const itemId = change.inventory_item_id;
          if (!changesByItemId[itemId]) {
            changesByItemId[itemId] = [];
          }
          changesByItemId[itemId].push(change);
        });

        // Fallback URL logic
        const now = new Date();
        const uploadToFilenameMap = new Map();
        const needsFallback = new Set();
        items.forEach((item) => {
          if (!uploadToFilenameMap.has(item.upload_log_id)) {
            uploadToFilenameMap.set(item.upload_log_id, item.filename || 'N/A');
          }
          const expiryDate = item.url_expiry ? new Date(item.url_expiry.replace(' ', 'T') + 'Z') : null;
          if (!item.signed_url || (expiryDate && expiryDate <= now)) {
            needsFallback.add(item.upload_log_id);
          }
        });

        const fallbackPromises = Array.from(needsFallback).map(async (uploadLogId) => {
          try { // Added try/catch to prevent stuck on URL error
            const filename = uploadToFilenameMap.get(uploadLogId);
            if (!filename || filename === 'N/A') return { uploadLogId, signedUrl: 'N/A' };
            if (filename === 'manual-tags') return { uploadLogId, signedUrl: 'N/A' };
            const path = `${clientId}/${saleId}/${filename}`;
            const signedUrl = await generateSignedUrl(path);
            if (signedUrl !== 'N/A') {
              const urlExpiry = new Date(now.getTime() + 7776000 * 1000).toISOString();
              await updateCachedUrl(uploadLogId, signedUrl, urlExpiry);
              return { uploadLogId, signedUrl };
            }
            return { uploadLogId, signedUrl: 'N/A' };
          } catch (err) {
            console.error('Fallback URL error for', uploadLogId, err);
            return { uploadLogId, signedUrl: 'N/A' };
          }
        });

        const fallbackResults = await Promise.all(fallbackPromises);
        const fallbackMap = new Map(fallbackResults.map(res => [res.uploadLogId, res.signedUrl]));

        // No client-side sort—RPC returns sorted by uploaded_at
        const inventoryData = items.map((item) => {
          const recentEdits = changesByItemId[item.id] || [];

          let latestTitleEdit = null;
          let latestPriceEdit = null;
          let latestQtyEdit = null;
          recentEdits.forEach(edit => {
            if (edit.field_name === 'title' && (!latestTitleEdit || new Date(edit.changed_at) > new Date(latestTitleEdit.changed_at))) {
              latestTitleEdit = edit;
            }
            if (edit.field_name === 'per_item_cost_estimate' && (!latestPriceEdit || new Date(edit.changed_at) > new Date(latestPriceEdit.changed_at))) {
              latestPriceEdit = edit;
            }
            if (edit.field_name === 'quantity_estimate' && (!latestQtyEdit || new Date(edit.changed_at) > new Date(latestQtyEdit.changed_at))) {
              latestQtyEdit = edit;
            }
          });

          const isTitleEdited = !!latestTitleEdit;
          const isPriceEdited = !!latestPriceEdit;
          const isQtyEdited = !!latestQtyEdit;

          let url = item.signed_url;
          if (fallbackMap.has(item.upload_log_id)) {
            url = fallbackMap.get(item.upload_log_id) || 'N/A';
          }

          return {
            id: item.id || '',
            section_name: item.section_name || 'N/A',
            category: item.category || 'N/A',
            title: item.title || 'N/A',
            description: item.description || 'N/A',
            story_points: item.story_points || 'N/A',
            resale_positioning: item.resale_positioning || 'N/A',
            pricing_context: item.pricing_context || 'N/A',
            item_index: typeof item.item_index === 'number' ? item.item_index : parseInt(item.item_index, 10) || 0,
            qr_token: item.qr_token ?? null,
            upload_log_id: item.upload_log_id ?? null,
            uploaded_at: item.uploaded_at ?? null,
            filename: item.filename || 'N/A',
            url,
            image_region: item.image_region || null,
            per_item_cost_estimate: toNumberOrNull(item.per_item_cost_estimate),
            quantity_estimate: toIntOr(item.quantity_estimate, 1),
            tagged: item.tagged || false,
            recentEdits,
            latestTitleEdit: latestTitleEdit || {},
            latestPriceEdit: latestPriceEdit || {},
            latestQtyEdit: latestQtyEdit || {},
            isTitleEdited,
            isPriceEdited,
            isQtyEdited,
          };
        });

        setInventory(inventoryData);
        setStatus('');
      } else {
        const itemErr = items?.error?.message || 'Unknown';
        const changesErr = changes?.error?.message || 'Unknown';
        setStatus(`Error: Failed to load inventory: ${itemErr || changesErr}. Contact support@scucciai.com`);
      }
    } catch (err) {
      console.error("useInventoryData load error:", err);
      setStatus(`Error: Failed to load inventory: ${err.message}. Contact support@scucciai.com`);
    } finally {
      setLoading(false);
    }
  }, [saleId, clientId, sectionId, fetchWrapper, supabase]);

  useEffect(() => {
    if (!saleId || !clientId || sectionId === undefined) return;
    load();
  }, [saleId, clientId, sectionId, load]);

  return {
    inventory,
    deletedItems,
    saleMeta,
    sections,
    loading,
    status,
    reload: load,
  };
}
